import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Api from "../../helpers/Api";
import {
  setResearchPatient,
  selectResearchPatient,
} from "../../store/slices/researchPatientSlice";
import { selectPatientsWithImages } from "../../store/slices/patientsWithImagesSlice";
import { selectUser } from "../../store/slices/userSlice";
import { selectIsCefalometria } from "../../store/slices/isCefalometriaSlice";

export function usePatientSearch() {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  // Params
  const idParams = searchParams.get("id") || searchParams.get("idClient");
  const seqAtendParams = searchParams.get("atd");
  const idPacienteParams = searchParams.get("idPacient");
  const ssParams = searchParams.get("ss");
  const pacienteNomeParams = searchParams.get("paciente");

  // Redux Selectors
  const { user } = useSelector(selectUser);
  const { isCefalometria } = useSelector(selectIsCefalometria);
  const { researchPatient } = useSelector(selectResearchPatient);
  const { patientsWithImages } = useSelector(selectPatientsWithImages);

  // Local State
  const [fetchedPatient, setFetchedPatient] = useState(null);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // Helper: Format Patient Image
  const formatPatientImage = (patient) => {
    if (!patient) return patient;
    const BASE_API_URL = API_URL || 'https://serapi.cdt.com.br';
    let imagemUrl = patient.imagem;
    
    if (imagemUrl && imagemUrl.includes('evolcloud')) {
      imagemUrl = null;
    }
    
    if (imagemUrl && imagemUrl.startsWith('/api/')) {
      imagemUrl = `${BASE_API_URL}${imagemUrl}`;
    }
    
    return {
      ...patient,
      imagem: imagemUrl,
      imagensPaciente: patient.imagensPaciente || []
    };
  };

  // Helper: Search in Normal API
  const searchInNormalApi = async (searchTerm, idClient, hash) => {
    try {
      const result = await Api.GetPatientList({ idClient, hash, page: 0 }, searchTerm);
      if (result.isSuccess && result.data?.patients?.length > 0) {
        return result.data.patients.find(
          (p) => p.seqAtend == seqAtendParams && 
                 (idPacienteParams ? p.idPaciente == idPacienteParams : true)
        ) || null;
      }
      return null;
    } catch (error) {
      console.error("Erro na busca API Normal:", error);
      return null;
    }
  };

  // Main Fetch Effect
  useEffect(() => {
    // 1. Validar se já temos o paciente carregado Localmente ou no Redux para evitar refetch
    // Mas apenas se bater com os parâmetros da URL
    const currentPatient = fetchedPatient || researchPatient;
    if (currentPatient && 
        currentPatient.seqAtend == seqAtendParams && 
        currentPatient.idPaciente == idPacienteParams) {
      if (!fetchedPatient && researchPatient) {
          // Se está no Redux mas não local, resincroniza localmente se necessário, 
          // ou apenas deixa o return final cuidar disso.
          // Mas vamos continuar para permitir atualização via SS se necessário? 
          // O código original parava se "pacientSearchCefx" (local) batesse.
          if (fetchedPatient) return;
      } else {
          return;
      }
    }

    const fetchData = async () => {
      setLoading(true);
      
      // === BUSCA VIA SS ===
      if (ssParams && idParams && seqAtendParams) {
        console.log("🟠 Iniciando busca via SS Param...");
        try {
          let qrCodeResult = await Api.GetPatientForQrCode(idParams, ssParams, seqAtendParams);
          if (!qrCodeResult.isSuccess || !qrCodeResult.data) {
              qrCodeResult = await Api.GetShared(ssParams);
          }
          
          if (qrCodeResult.isSuccess && qrCodeResult.data) {
             const resultImages = await Api.GetImagensPatient(
               qrCodeResult.data.idClient, 
               qrCodeResult.data.idPaciente || idPacienteParams, 
               qrCodeResult.data.seqAtend
             );

             let imagens = [];
             if (resultImages.isSuccess) {
                 const API_BASE = API_URL || 'https://serapi.cdt.com.br';
                 imagens = resultImages.data.filter(imagem => {
                    const link = imagem.link || '';
                    if (link.startsWith('https')) {
                      return ((link.includes('/img') || imagem.nome.endsWith('.dfx') || imagem.nome.endsWith('.zip')) && !imagem.nome.includes('tomo_m'));
                    }
                    return !imagem.nome.includes('tomo_m');
                  }).map(imagem => ({
                      ...imagem,
                      link: imagem.link?.startsWith('https') ? imagem.link : `${API_BASE}${imagem.link || ''}`,
                      icone: imagem.icone?.startsWith('https') ? imagem.icone : `${API_BASE}${imagem.icone || ''}`,
                      linkDownload: imagem.linkDownload ? `${API_BASE}${imagem.linkDownload}` : ''
                  }));
             }

             const pacienteViaSS = {
                ...qrCodeResult.data,
                id: qrCodeResult.data.id,
                idPaciente: qrCodeResult.data.idPaciente,
                seqAtend: qrCodeResult.data.seqAtend,
                idClient: qrCodeResult.data.idClient || idParams,
                nome: qrCodeResult.data.paciente || qrCodeResult.data.nome,
                imagem: qrCodeResult.data.imagem ? `${API_URL || ''}${qrCodeResult.data.imagem}` : null,
                imagensPaciente: imagens,
                exames: qrCodeResult.data.exames || []
             };

             dispatch(setResearchPatient(pacienteViaSS));
             setFetchedPatient(pacienteViaSS);
             setLoading(false);
             return;
          }
        } catch (error) {
          console.error("Erro na busca via SS:", error);
        }
      }

      // === BUSCA CEFX / NORMAL DO PAGE LOAD ===
      if (isCefalometria && seqAtendParams && user) {
          const filteredUser = user.find((u) => u.idClient === idParams);
          if (!filteredUser) {
            setLoading(false);
            return;
          }

          const { hash, idClient } = filteredUser;
          let foundPatient = null;

          // Etapa 1: Busca Cefx
          try {
             const cefxResult = await Api.GetPatientListCefx(
                { idClient, hash, page: 0, seqAtend: seqAtendParams },
                { process_id: -2, to_conclude: -1 },
                { searchName: seqAtendParams }
             );
             if (cefxResult.isSuccess && cefxResult.data?.patients) {
                 const check = (list) => list.find(p => p.seqAtend == seqAtendParams && (idPacienteParams ? p.idPaciente == idPacienteParams : true));
                 foundPatient = check(cefxResult.data.patients);
                 
                 // Paginação se não encontrou na primeira
                 if (!foundPatient && cefxResult.data.totalPages > 1) {
                     for (let p = 1; p < cefxResult.data.totalPages && !foundPatient; p++) {
                         const pageRes = await Api.GetPatientListCefx({ idClient, hash, page: p }, { process_id: -2, to_conclude: -1 });
                         if (pageRes.isSuccess) foundPatient = check(pageRes.data.patients);
                     }
                 }
             }
          } catch (e) { console.error(e); }

          // Etapa 2: API Normal
          if (!foundPatient) {
              foundPatient = await searchInNormalApi(seqAtendParams, idClient, hash);
              if (!foundPatient && idPacienteParams) foundPatient = await searchInNormalApi(idPacienteParams, idClient, hash);
              if (!foundPatient && pacienteNomeParams) {
                  const decodedName = decodeURIComponent(pacienteNomeParams).replace(/_/g, ' ');
                  foundPatient = await searchInNormalApi(decodedName, idClient, hash);
              }
          }

          // Etapa 3: Nome Cefx
          if (!foundPatient && pacienteNomeParams) {
              try {
                  const decodedName = decodeURIComponent(pacienteNomeParams).replace(/_/g, ' ');
                  const res = await Api.GetPatientListCefx({ idClient, hash, page: 0 }, { process_id: -2, to_conclude: -1 }, { searchName: decodedName });
                  if (res.isSuccess) {
                      foundPatient = res.data?.patients?.find(p => p.seqAtend == seqAtendParams);
                  }
              } catch (e) { console.error(e); }
          }

          if (foundPatient) {
              const normalized = {
                  ...foundPatient,
                  idClient: foundPatient.idClient || idParams,
                  nome: foundPatient.paciente || foundPatient.nome,
                  imagensPaciente: foundPatient.imagensPaciente || [],
                  exames: foundPatient.exames || []
              };
              const final = formatPatientImage(normalized);
              dispatch(setResearchPatient(final));
              setFetchedPatient(final);
          }
      }
      setLoading(false);
    };

    fetchData();
  }, [isCefalometria, user, seqAtendParams, idPacienteParams, ssParams]); // Removido dependencies irrelevantes para loop

  // Decisão final de qual paciente retornar
  // 1. Fetched localmente (mais fresco)
  // 2. Redux (se bater params)
  // 3. Lista cached (se bater params)
  
  const getFinalPatient = () => {
    if (fetchedPatient && 
        fetchedPatient.seqAtend == seqAtendParams && 
        (fetchedPatient.idPaciente == idPacienteParams || !idPacienteParams)) {
        return fetchedPatient;
    }

    if (researchPatient && 
        researchPatient.seqAtend == seqAtendParams &&
        researchPatient.idPaciente == idPacienteParams) {
        return researchPatient;
    }
    
    // Fallback lista
    return patientsWithImages.find(p => 
        p.seqAtend == seqAtendParams && 
        p.idPaciente == idPacienteParams && 
        (p.idClient == idParams || !p.idClient || !idParams)
    );
  };

  return { 
      patient: getFinalPatient(), 
      loading 
  };
}
